import { z } from 'zod';
import { createPaginationSchema, objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE PLACE MASTER (FR-5.7) — a shared gazetteer, GLOBAL across the commissionerate.
 *
 *  ⚠️ GLOBAL READ IS THE DESIGN, NOT AN OVERSIGHT. Anyone with dashboard access searches
 *  the whole gazetteer, with no subtree filter anywhere behind it. A place existing is
 *  not sensitive; a police arrangement at that place is, and FR-3.6's isolation lives on
 *  the arrangement. Scoping this collection would fragment it per division, and a
 *  gazetteer that fragments is one nobody's autocomplete ever learns from.
 *
 *  Written down here, in `CAPABILITIES`, in `services/place.service.ts` and in
 *  CLAUDE.md §10 — four places, because it reads like a bug and is not one.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * Where the record came from, which is what decides how much to trust its coordinates.
 *
 *   MANUAL   an operator typed it. The only source P1b can produce.
 *   EXTERNAL a geocoding provider returned it, with an `externalPlaceId`. The provider
 *            interface exists and is a no-op; see `services/geocoding/`.
 *   DERIVED  its position was computed from observed duty coordinates (P5).
 */
export const PLACE_SOURCES = ['MANUAL', 'EXTERNAL', 'DERIVED'] as const;
export type PlaceSource = (typeof PLACE_SOURCES)[number];

/** WGS84, in the order a human says them — the model stores GeoJSON `[lng, lat]`. */
export const coordinatesSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

/**
 * [PLACE-500] THE GAZETTEER'S OWN NAME LIMIT — a place MASTER row, never a duty point's free text, which takes
 * `ARRANGEMENT_LIMITS.PLACE_NAME` (500). A typed duty place longer than this can match no master row, so the
 * builder does not ask the suggestion probe about it (`PlaceAutocomplete`).
 */
export const PLACE_MASTER_NAME_MAX = 200;

export const placeBaseSchema = z.object({
  name: z
    .string()
    .min(1, 'Place name is required')
    .max(PLACE_MASTER_NAME_MAX, 'Place name is too long')
    // Same treatment as a person's name: collapse internal whitespace, trim the ends,
    // and LEAVE THE CASE ALONE. "PS" and "Ps" are not the same word to a reader.
    .transform((raw) => raw.trim().replace(/\s+/g, ' '))
    .refine((value) => value.length > 0, 'Place name is required'),
  /**
   * Other names the same place goes by — a former name, a transliteration, a local
   * shorthand. These are searched exactly as the name is, which is what makes a merge
   * non-destructive: the absorbed place's name becomes an alias and keeps answering.
   */
  /**
   * ⚠️ [amended P2] NO `.default([])` HERE. It lives on `createPlaceSchema` below.
   *
   * In zod 4 `.partial()` does NOT strip a `.default()` — it produces
   * `ZodOptional<ZodDefault<…>>`, and the default still fires on a missing key. With the
   * default on this base schema, `updatePlaceSchema.safeParse({})` succeeded with
   * `{ aliases: [] }`, so `PATCH /places/:id` with an empty body returned 200 and
   * `updatePlace`'s `input.aliases ?? doc.aliases` saw `[]` rather than `undefined` —
   * WIPING the alias list and recomputing `search_terms` without it. That is exactly the
   * silent stop-answering-autocomplete failure this file warns about, arriving through
   * the front door.
   *
   * The rule, which CLAUDE.md §6 now states: a base schema that gets `.partial()`-ed
   * carries no defaults. Defaults go on the create schema, added with `.extend`.
   */
  aliases: z.array(z.string().trim().min(1).max(200)).max(25),
  formattedAddress: z.string().trim().min(1).max(500).optional(),
  coords: coordinatesSchema.optional(),
});

export const createPlaceSchema = placeBaseSchema.extend({
  aliases: placeBaseSchema.shape.aliases.default([]),
  /**
   * ⚠️ The near-duplicate backstop. Absent or false, a create whose name is close to an
   * existing place is REFUSED with 409 and the candidates in `error.details.suggestions`.
   * Setting it true creates anyway, and that is always permitted — the prompt is the
   * point, not the block (FR-5.7.3).
   *
   * It exists as a body field rather than only as a UI courtesy so that the prompt is
   * structural: a caller that never renders the suggestion screen still has to see the
   * candidates once before it can insist.
   */
  confirmDuplicate: z.boolean().default(false),
});

export const updatePlaceSchema = placeBaseSchema
  .partial()
  .extend({ confirmDuplicate: z.boolean().default(false) })
  .refine((value) => Object.keys(value).some((key) => key !== 'confirmDuplicate'), {
    message: 'At least one field must be provided',
  });

/** Merge B into A. A is the `:id` in the path; B is named here and is soft-deleted. */
export const mergePlaceSchema = z.object({
  sourceId: objectIdSchema,
});

export const placeSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  aliases: z.array(z.string()),
  formattedAddress: z.string().optional(),
  coords: coordinatesSchema.optional(),
  source: z.enum(PLACE_SOURCES),
  externalPlaceId: z.string().optional(),
  createdByOfficerId: objectIdSchema,
  createdByOrgUnitId: objectIdSchema,
  /** How often this place has been chosen. Orders autocomplete, and nothing else. */
  usageCount: z.number().int().min(0),
  lastUsedAt: z.iso.datetime().optional(),
  isActive: z.boolean(),
  /** Set when this place has been merged INTO another; reads resolve through to it. */
  mergedIntoId: objectIdSchema.optional(),
  /** What this place absorbed. Empty for almost every row. */
  mergedFrom: z.array(
    z.object({
      placeId: objectIdSchema,
      name: z.string(),
      aliases: z.array(z.string()),
      usageCount: z.number().int().min(0),
      mergedAt: z.iso.datetime(),
      mergedByOfficerId: objectIdSchema,
    }),
  ),
  /** Duty observations behind a DERIVED position (P5). Zero until duty events exist. */
  sampleCount: z.number().int().min(0),
  derivedCentroid: coordinatesSchema.optional(),
  /** Metres. How far apart the observations are — a wide spread means a vague place. */
  spreadMetres: z.number().min(0).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** Embedded wherever a place is referenced, so a list needs no second round trip. */
export const placeSummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  formattedAddress: z.string().optional(),
});

/** Every value here has a supporting index on the model (CLAUDE.md §7). */
export const PLACE_SORT_FIELDS = ['name', 'usageCount', 'lastUsedAt', 'createdAt'] as const;
export type PlaceSortField = (typeof PLACE_SORT_FIELDS)[number];

export const listPlacesQuerySchema = createPaginationSchema(PLACE_SORT_FIELDS, 'usageCount')
  .extend({
    /**
     * PREFIX-matched against the name and every alias, token by token. Not a substring
     * scan and not `$text` — see `services/place.service.ts` for why neither works.
     */
    search: z.string().trim().min(1).max(200).optional(),
    source: z.enum(PLACE_SOURCES).optional(),
    includeInactive: z.stringbool().default(false),
  })
  // Most-used first is what an autocomplete wants; `asc` on `usageCount` would put the
  // places nobody has ever chosen at the top of every list.
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('desc') });

/**
 * The near-duplicate probe. Same detector the create backstop runs, exposed so the UI
 * can ask BEFORE submitting and show "Did you mean X?" while there is still a form to
 * correct.
 */
export const placeSuggestionsQuerySchema = z.object({
  name: z.string().trim().min(1).max(PLACE_MASTER_NAME_MAX),
});

export const placeSuggestionSchema = z.object({
  place: placeSummarySchema,
  /** 1 = the normalised names are identical. The threshold lives on the server. */
  score: z.number().min(0).max(1),
  reason: z.enum(['EXACT_MATCH', 'SIMILAR_NAME', 'CONTAINS_NAME']),
});

export const placeSuggestionsResponseSchema = z.object({
  suggestions: z.array(placeSuggestionSchema),
});
